import {
  BMI_THRESHOLDS,
  NUTRITION_LABELS,
  WHTR_THRESHOLDS,
} from '../constants/nutrition';
import type {
  PatientBaseProfile,
  PatientMeasurementMetricType,
  PatientMeasurementRecord,
  PatientNutritionMetricRow,
  PatientNutritionMetrics,
} from '../types';

interface PatientNutritionInput {
  biologicalSex: PatientBaseProfile['biologicalSex'];
  heightCm: number | null;
  age: number | null;
  weightKg: number | null;
  waistCm: number | null;
  chestCm: number | null;
  neckCm: number | null;
}

const round = (value: number | null, decimals: number = 2) => {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }

  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const isPositive = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const parseMeasurementValue = (value: string | null | undefined) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const normalized = value.replace(',', '.').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getMetricFieldValue = (
  measurement: PatientMeasurementRecord | null,
  metricType: Exclude<PatientMeasurementMetricType, null>
) => {
  if (measurement == null) {
    return null;
  }

  const field = measurement.fields.find(candidate => candidate.metricType === metricType);
  return parseMeasurementValue(field?.value);
};

export const deriveAgeFromDateOfBirth = (dateOfBirth: string | null) => {
  if (dateOfBirth == null) {
    return null;
  }

  const trimmed = dateOfBirth.trim();
  const yearOnly = trimmed.match(/^(\d{4})$/);
  const fullDate = trimmed.match(/^(\d{4})-\d{2}-\d{2}/);
  const birthYear = Number(yearOnly?.[1] ?? fullDate?.[1]);

  if (!Number.isInteger(birthYear)) {
    return null;
  }

  const currentYear = new Date().getFullYear();
  if (birthYear < 1900 || birthYear > currentYear) {
    return null;
  }

  // Year-only / spannometric age (no month/day precision).
  return currentYear - birthYear;
};

export const buildNutritionInput = (
  profile: PatientBaseProfile,
  measurement: PatientMeasurementRecord | null
): PatientNutritionInput => ({
  biologicalSex: profile.biologicalSex,
  heightCm: profile.heightCm,
  age: deriveAgeFromDateOfBirth(profile.dateOfBirth),
  weightKg: getMetricFieldValue(measurement, 'weight_kg'),
  waistCm: getMetricFieldValue(measurement, 'waist_cm'),
  chestCm: getMetricFieldValue(measurement, 'chest_cm'),
  neckCm: getMetricFieldValue(measurement, 'neck_cm'),
});

const getBmiCategory = (bmi: number | null) => {
  if (bmi == null) return null;
  if (bmi < BMI_THRESHOLDS.underweight) return 'Sottopeso';
  if (bmi <= BMI_THRESHOLDS.normalUpper) return 'Normopeso';
  if (bmi <= BMI_THRESHOLDS.overweightUpper) return 'Sovrappeso';
  return 'Obesità';
};

const getWaistToHeightCategory = (ratio: number | null) => {
  if (ratio == null) return null;
  if (ratio < WHTR_THRESHOLDS.low) return 'Basso';
  if (ratio <= WHTR_THRESHOLDS.normalUpper) return 'Nella norma';
  if (ratio <= WHTR_THRESHOLDS.highUpper) return 'Aumentato';
  return 'Molto aumentato';
};

const estimateBodyFatPercentage = (input: PatientNutritionInput) => {
  const { biologicalSex, heightCm, waistCm } = input;

  if (biologicalSex == null || !isPositive(heightCm) || !isPositive(waistCm)) {
    return null;
  }

  const value =
    biologicalSex === 'male'
      ? 64 - 20 * (heightCm / waistCm)
      : 76 - 20 * (heightCm / waistCm);

  return round(Math.max(value, 0));
};

export const calculatePatientNutritionMetrics = (
  input: PatientNutritionInput | null
): PatientNutritionMetrics => {
  if (input == null) {
    return {
      bmi: null,
      bmiCategory: null,
      normalWeightMinKg: null,
      normalWeightMaxKg: null,
      weightDeltaToNormalKg: null,
      waistToHeightRatio: null,
      waistToHeightCategory: null,
      waistToChestRatio: null,
      bodySurfaceAreaM2: null,
      bodyFatPercentage: null,
      fatMassKg: null,
      leanMassKg: null,
      rows: [],
    };
  }

  const heightM = isPositive(input.heightCm) ? input.heightCm / 100 : null;
  const bmi =
    heightM != null && isPositive(input.weightKg)
      ? round(input.weightKg / heightM ** 2)
      : null;
  const bmiCategory = getBmiCategory(bmi);

  const normalWeightMinKg =
    heightM != null ? round(BMI_THRESHOLDS.underweight * heightM ** 2) : null;
  const normalWeightMaxKg =
    heightM != null ? round(BMI_THRESHOLDS.normalUpper * heightM ** 2) : null;

  let weightDeltaToNormalKg: number | null = null;
  if (
    isPositive(input.weightKg) &&
    normalWeightMinKg != null &&
    normalWeightMaxKg != null
  ) {
    if (input.weightKg < normalWeightMinKg) {
      weightDeltaToNormalKg = round(input.weightKg - normalWeightMinKg);
    } else if (input.weightKg > normalWeightMaxKg) {
      weightDeltaToNormalKg = round(input.weightKg - normalWeightMaxKg);
    } else {
      weightDeltaToNormalKg = 0;
    }
  }

  const waistToHeightRatio =
    isPositive(input.waistCm) && isPositive(input.heightCm)
      ? round(input.waistCm / input.heightCm, 3)
      : null;
  const waistToHeightCategory = getWaistToHeightCategory(waistToHeightRatio);

  const waistToChestRatio =
    isPositive(input.waistCm) && isPositive(input.chestCm)
      ? round(input.waistCm / input.chestCm, 3)
      : null;

  const bodySurfaceAreaM2 =
    isPositive(input.heightCm) && isPositive(input.weightKg)
      ? round(Math.sqrt((input.heightCm * input.weightKg) / 3600), 3)
      : null;

  const bodyFatPercentage = estimateBodyFatPercentage(input);
  const fatMassKg =
    isPositive(input.weightKg) && bodyFatPercentage != null
      ? round((input.weightKg * bodyFatPercentage) / 100)
      : null;
  const leanMassKg =
    isPositive(input.weightKg) && fatMassKg != null
      ? round(input.weightKg - fatMassKg)
      : null;

  const rows: PatientNutritionMetricRow[] = [
    {
      key: 'bmi',
      label: NUTRITION_LABELS.bmi,
      formula: 'peso (kg) / altezza² (m)',
      value: bmi,
      unit: 'kg/m²',
      interpretation: bmiCategory,
    },
    {
      key: 'normalWeightRange',
      label: NUTRITION_LABELS.normalWeightRange,
      formula: 'BMI 18.5 - 24.9',
      value:
        normalWeightMinKg != null && normalWeightMaxKg != null
          ? round(normalWeightMaxKg - normalWeightMinKg)
          : null,
      unit: 'kg span',
      interpretation:
        normalWeightMinKg != null && normalWeightMaxKg != null
          ? `${normalWeightMinKg.toFixed(2)} - ${normalWeightMaxKg.toFixed(2)} kg`
          : null,
    },
    {
      key: 'deltaToNormalWeight',
      label: NUTRITION_LABELS.deltaToNormalWeight,
      formula: 'peso attuale - limite normopeso',
      value: weightDeltaToNormalKg,
      unit: 'kg',
      interpretation:
        weightDeltaToNormalKg == null
          ? null
          : weightDeltaToNormalKg === 0
            ? 'Nel range normopeso'
            : weightDeltaToNormalKg > 0
              ? 'Sopra il range'
              : 'Sotto il range',
    },
    {
      key: 'waistToHeightRatio',
      label: NUTRITION_LABELS.waistToHeightRatio,
      formula: 'vita / altezza',
      value: waistToHeightRatio,
      unit: 'ratio',
      interpretation: waistToHeightCategory,
    },
    {
      key: 'waistToChestRatio',
      label: NUTRITION_LABELS.waistToChestRatio,
      formula: 'vita / petto',
      value: waistToChestRatio,
      unit: 'ratio',
      interpretation: waistToChestRatio == null ? null : 'Indicatore proporzionale',
    },
    {
      key: 'bodySurfaceArea',
      label: NUTRITION_LABELS.bodySurfaceArea,
      formula: 'sqrt((altezza * peso) / 3600)',
      value: bodySurfaceAreaM2,
      unit: 'm²',
      interpretation: bodySurfaceAreaM2 == null ? null : 'Formula Mosteller',
    },
    {
      key: 'bodyFatPercentage',
      label: NUTRITION_LABELS.bodyFatPercentage,
      formula: 'Relative Fat Mass',
      value: bodyFatPercentage,
      unit: '%',
      interpretation:
        bodyFatPercentage == null
          ? 'Richiede sesso, altezza e vita'
          : 'Stima antropometrica',
    },
    {
      key: 'fatMassKg',
      label: NUTRITION_LABELS.fatMassKg,
      formula: 'peso * body fat %',
      value: fatMassKg,
      unit: 'kg',
      interpretation:
        fatMassKg == null ? '-' : 'Stima derivata da body fat %',
    },
    {
      key: 'leanMassKg',
      label: NUTRITION_LABELS.leanMassKg,
      formula: 'peso - massa grassa',
      value: leanMassKg,
      unit: 'kg',
      interpretation:
        leanMassKg == null ? '-' : 'Stima derivata da body fat %',
    },
  ];

  return {
    bmi,
    bmiCategory,
    normalWeightMinKg,
    normalWeightMaxKg,
    weightDeltaToNormalKg,
    waistToHeightRatio,
    waistToHeightCategory,
    waistToChestRatio,
    bodySurfaceAreaM2,
    bodyFatPercentage,
    fatMassKg,
    leanMassKg,
    rows,
  };
};
